import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const goalOptions = [
  { label: 'Get promoted', value: 'Get promoted' },
  { label: 'Negotiate a raise', value: 'Negotiate a raise' },
  { label: 'Ace my performance review', value: 'Ace my performance review' },
  { label: 'Prepare for a job interview', value: 'Prepare for a job interview' },
  { label: 'Keeping a record of my wins', value: 'Keeping a record of my wins' },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1 state
  const [jobTitle, setJobTitle] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');

  // Step 2 state
  const [winText, setWinText] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedWin, setEnhancedWin] = useState(null);
  const [enhanceError, setEnhanceError] = useState(null);
  const [completing, setCompleting] = useState(false);

  const completeOnboarding = async () => {
    try {
      await axios.post(
        `${API}/users/onboarding`,
        { job_title: jobTitle, career_goals: [selectedGoal] },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
    } catch (err) {
      console.error('Onboarding completion error:', err);
      // Don't block navigation on this error
    }
  };

  const handleContinueToStep2 = () => {
    setStep(2);
  };

  const handleEnhanceWin = async () => {
    setEnhancing(true);
    setEnhancedWin(null);
    setEnhanceError(null);
    try {
      // Save job title and goal first so the polish reads the user's real profile,
      // not the generic default.
      await completeOnboarding();
      const response = await axios.post(
        `${API}/wins`,
        {
          original_text: winText,
          category: [selectedGoal],
          win_date: new Date().toISOString(),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      if (!response.data || !response.data.enhanced_text) {
        setEnhanceError("We couldn't polish this win. Try again, or paste in a shorter version.");
        return;
      }
      setEnhancedWin(response.data);
    } catch (err) {
      console.error('Win enhancement error:', err);
      setEnhanceError("We couldn't polish this win. Try again, or paste in a shorter version.");
    } finally {
      setEnhancing(false);
    }
  };

  const handleFinish = async () => {
    setCompleting(true);
    await completeOnboarding();
    // Use window.location instead of navigate() to force a clean page load
    // after the onboarding_completed flag is written to DB, avoids ProtectedRoute
    // race condition where it re-fetches auth/me before the DB write settles
    window.location.href = '/dashboard';
  };

  const handleSkip = async () => {
    setCompleting(true);
    await completeOnboarding();
    window.location.href = '/dashboard';
  };

  const step1Valid = jobTitle.trim().length > 0 && selectedGoal.length > 0;
  const winLongEnough = winText.trim().length >= 20;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-start px-4 py-10">
      {/* Logo */}
      <img src="/assets/bragbox-hero.png" alt="BragBox" className="h-10 mx-auto mb-6" />

      {/* Progress bar */}
      <div className="w-full max-w-lg mb-8">
        <div className="flex justify-between text-xs font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
          <span>Step {step} of 2</span>
          <span>{step === 1 ? 'About you' : 'Your first win'}</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{
              width: step === 1 ? '50%' : '100%',
              backgroundColor: 'var(--navy-900)',
            }}
          />
        </div>
      </div>

      <div className="w-full max-w-lg">
        {step === 1 && (
          <div>
            <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }}>
              Let's set the stage
            </h1>
            <p className="text-gray-500 text-center mb-8 text-sm">
              Two quick questions and you're in.
            </p>

            {/* Job Title */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--navy-900)' }}>
                What's your job title?
              </label>
              <Input
                type="text"
                placeholder="e.g. Senior Product Manager"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full text-base py-3"
                autoFocus
              />
            </div>

            {/* Goal selection */}
            <div className="mb-8">
              <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--navy-900)' }}>
                What are you working toward?
              </label>
              <div className="grid grid-cols-1 gap-3">
                {goalOptions.map((goal) => {
                  const isSelected = selectedGoal === goal.value;
                  return (
                    <button
                      key={goal.value}
                      onClick={() => setSelectedGoal(goal.value)}
                      className="flex items-center gap-3 w-full text-left px-4 py-4 rounded-xl border-2 transition-all duration-150 font-medium text-sm"
                      style={{
                        borderColor: isSelected ? 'var(--navy-900)' : '#e5e7eb',
                        backgroundColor: isSelected ? 'var(--navy-900)' : 'white',
                        color: isSelected ? 'white' : 'var(--navy-900)',
                        boxShadow: isSelected ? '0 2px 12px rgba(0,0,0,0.10)' : 'none',
                      }}
                    >
                      <span>{goal.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={handleContinueToStep2}
              disabled={!step1Valid}
              className="w-full py-4 text-base font-semibold rounded-xl transition-all"
              style={{
                backgroundColor: step1Valid ? 'var(--navy-900)' : '#d1d5db',
                color: 'white',
                cursor: step1Valid ? 'pointer' : 'not-allowed',
              }}
            >
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }}>
              Log your first win. Watch what happens.
            </h1>
            <p className="text-gray-500 text-center mb-8 text-sm">
              Type anything. Big or small. The AI will show you what it sounds like in a performance review.
            </p>

            {/* Win input */}
            <div className="mb-4">
              <textarea
                className="w-full rounded-xl border-2 border-gray-200 p-4 text-base resize-none focus:outline-none focus:border-navy-900 transition-colors"
                style={{ minHeight: '140px', borderColor: winText.length > 0 ? 'var(--navy-900)' : '#e5e7eb' }}
                placeholder="e.g. Led the redesign of our checkout flow..."
                value={winText}
                onChange={(e) => setWinText(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{winText.length} / 20 characters</p>
            </div>

            {/* Enhance button: always visible, disabled until 20+ chars (Fix #2: discoverability) */}
            {!enhancedWin && !enhancing && (
              <Button
                onClick={handleEnhanceWin}
                disabled={!winLongEnough}
                className="w-full py-4 text-base font-semibold rounded-xl mb-4 transition-opacity"
                style={{
                  backgroundColor: 'var(--navy-900)',
                  color: 'white',
                  opacity: winLongEnough ? 1 : 0.5,
                  cursor: winLongEnough ? 'pointer' : 'not-allowed',
                }}
              >
                Polish this win
              </Button>
            )}

            {/* Inline polish error */}
            {enhanceError && !enhancing && (
              <div className="mb-4 rounded-xl border-2 border-red-200 bg-red-50 p-4" data-testid="enhance-error">
                <p className="text-sm text-red-700">{enhanceError}</p>
              </div>
            )}

            {/* Loading state */}
            {enhancing && (
              <div className="w-full flex flex-col items-center justify-center py-8 mb-4">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-gray-300 mb-3" style={{ borderTopColor: 'var(--navy-900)' }} />
                <p className="text-sm text-gray-500 font-medium">Polishing your win...</p>
              </div>
            )}

            {/* Before / After reveal */}
            {enhancedWin && (
              <div className="mb-6">
                <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2">
                  {/* Before */}
                  <div className="rounded-xl border-2 border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-400">Before</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{enhancedWin.original_text}</p>
                  </div>

                  {/* After */}
                  <div
                    className="rounded-xl border-2 p-4"
                    style={{ borderColor: '#C4A35A', backgroundColor: '#fdf6e3' }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--navy-900)' }}>
                        After (polished)
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed font-medium" style={{ color: 'var(--navy-900)' }}>
                      {enhancedWin.enhanced_text}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={handleFinish}
                  disabled={completing}
                  className="w-full py-4 text-base font-semibold rounded-xl"
                  style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                >
                  {completing ? 'Setting up your dashboard...' : 'This is going in your dashboard. Let\'s go →'}
                </Button>
              </div>
            )}

            {/* Skip link: shows when less than 20 chars or as an alternative */}
            {!enhancedWin && (
              <div className="text-center mt-4">
                <button
                  onClick={handleSkip}
                  disabled={completing}
                  className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
                >
                  {completing ? 'Going to dashboard...' : 'Skip and go to dashboard'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
